import { useEffect, useState } from 'react';
import { getPortalPaystubs, getMyPay, type PortalPaystub, type MyPay } from '@lib/api/portal';
import { PullToRefresh } from '@/components/common';

const fmt = (n: number | string) => `$${Number(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d: string) => new Date(d + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
const fmtDateShort = (d: string) => new Date(d + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });

export const PaystubsPage = () => {
  const [paystubs, setPaystubs] = useState<PortalPaystub[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  // #18 – copy net pay state
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [myPay, setMyPay] = useState<MyPay | null>(null);

  const load = () => {
    setLoading(true);
    getMyPay().then(setMyPay).catch(() => {});
    getPortalPaystubs()
      .then(setPaystubs)
      .catch(() => setError('Failed to load paystubs. Please refresh.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // #18 – copy net pay to clipboard
  const copyNetPay = async (p: PortalPaystub) => {
    try {
      await navigator.clipboard.writeText(fmt(p.net_pay));
      setCopiedId(p.id);
      setTimeout(() => setCopiedId(null), 2500);
    } catch { /* ignore */ }
  };

  if (loading) return (
    <div className="space-y-3">
      {[1, 2, 3].map(i => (
        <div key={i} className="h-24 animate-pulse rounded-xl bg-gray-100" />
      ))}
    </div>
  );
  if (error) return <div className="px-4 py-6 text-sm text-red-600 flex items-center gap-2 bg-red-50 rounded-xl border border-red-200"><span className="material-symbols-outlined text-base">error</span>{error}</div>;

  return (
    // #18 – PullToRefresh on paystubs list
    <PullToRefresh onRefresh={load}>
      <div className="space-y-4">
        <h1 className="text-xl font-bold text-warm-charcoal">My Pay</h1>

        {/* Earned / paid / owed summary */}
        {myPay?.summary && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-[11px] text-gray-500">Earned</p>
                <p className="text-base font-bold text-gray-900">{fmt(myPay.summary.earned)}</p>
                <p className="text-[10px] text-gray-400">{myPay.summary.approved_hours.toFixed(1)}h approved</p>
              </div>
              <div>
                <p className="text-[11px] text-gray-500">Paid to you</p>
                <p className="text-base font-bold text-gray-900">{fmt(myPay.summary.paid)}</p>
              </div>
              <div className={`rounded-lg py-1 ${myPay.summary.owed > 0.005 ? 'bg-[#C77D3F]/10' : 'bg-green-50'}`}>
                <p className={`text-[11px] ${myPay.summary.owed > 0.005 ? 'text-[#7a4a22]' : 'text-green-700'}`}>Owed to you</p>
                <p className={`text-base font-bold ${myPay.summary.owed > 0.005 ? 'text-[#C77D3F]' : 'text-green-700'}`}>{fmt(myPay.summary.owed)}</p>
              </div>
            </div>
            {myPay.summary.pending_hours > 0 && (
              <p className="text-[11px] text-gray-400 text-center">
                + {myPay.summary.pending_hours.toFixed(1)}h submitted, waiting for approval (not counted yet)
              </p>
            )}
            {/* Payment history */}
            {myPay.payouts.length > 0 && (
              <div className="border-t border-gray-100 pt-2">
                <p className="text-xs font-semibold text-gray-500 mb-1.5">Payments received</p>
                <div className="space-y-1.5">
                  {myPay.payouts.slice(0, 8).map(p => (
                    <div key={p.id} className="flex items-center gap-2 text-sm">
                      <span className="material-symbols-outlined text-green-600 text-base">paid</span>
                      <span className="font-medium text-gray-900">{fmt(p.amount)}</span>
                      <span className="text-xs text-gray-400 ml-auto">
                        {fmtDate(String(p.paid_on).slice(0, 10))} · {p.method}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Paystubs</h2>
        {paystubs.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <span className="material-symbols-outlined text-gray-300 text-4xl">payments</span>
            <p className="text-sm text-gray-500 mt-2">No paystubs yet.</p>
            <p className="text-xs text-gray-400 mt-1">Approved payroll runs will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {paystubs.map(p => {
              const isOpen = expanded === p.id;
              const ot = Number(p.ot_1_5_hours) > 0 || Number(p.ot_2_0_hours) > 0;
              return (
                <div key={p.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <button
                    className="w-full text-left p-4"
                    onClick={() => setExpanded(isOpen ? null : p.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 text-sm">
                          {fmtDateShort(p.pay_period_start)} – {fmtDate(p.pay_period_end)}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Pay date: {fmtDate(p.pay_date)}
                          {p.pay_frequency ? ` · ${p.pay_frequency}` : ''}
                        </p>
                      </div>
                      <div className="text-right ml-3 shrink-0 flex items-start gap-2">
                        <div>
                          <p className="text-lg font-bold text-gray-900">{fmt(p.net_pay)}</p>
                          <p className="text-xs text-gray-400">net pay</p>
                        </div>
                        {/* #18 – copy net pay to clipboard */}
                        <button
                          type="button"
                          onClick={e => { e.stopPropagation(); copyNetPay(p); }}
                          title="Copy net pay"
                          className="mt-0.5 text-gray-400 hover:text-[#C77D3F] transition-colors"
                        >
                          <span className="material-symbols-outlined text-sm">
                            {copiedId === p.id ? 'check' : 'content_copy'}
                          </span>
                        </button>
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                      <div className="bg-gray-50 rounded-lg p-2">
                        <p className="text-gray-500">Gross</p>
                        <p className="font-semibold text-gray-800">{fmt(p.gross_pay)}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2">
                        <p className="text-gray-500">Deductions</p>
                        <p className="font-semibold text-gray-800">{fmt(p.total_deductions)}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2">
                        <p className="text-gray-500">Hours</p>
                        <p className="font-semibold text-gray-800">
                          {Number(p.regular_hours).toFixed(1)}
                          {ot ? <span className="text-amber-600"> +OT</span> : ''}
                        </p>
                      </div>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-gray-100 px-4 pb-4 pt-3 space-y-3">
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Earnings</p>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Regular ({Number(p.regular_hours).toFixed(1)} h @ {fmt(p.hourly_rate)}/h)</span>
                            <span className="font-medium">{fmt(p.regular_pay ?? 0)}</span>
                          </div>
                          {Number(p.ot_1_5_hours) > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">OT 1.5× ({Number(p.ot_1_5_hours).toFixed(1)} h)</span>
                              <span className="font-medium">{fmt(p.ot_1_5_pay ?? 0)}</span>
                            </div>
                          )}
                          {Number(p.ot_2_0_hours) > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">OT 2.0× ({Number(p.ot_2_0_hours).toFixed(1)} h)</span>
                              <span className="font-medium">{fmt(p.ot_2_0_pay ?? 0)}</span>
                            </div>
                          )}
                          <div className="flex justify-between font-semibold border-t border-gray-100 pt-1">
                            <span>Gross Pay</span>
                            <span>{fmt(p.gross_pay)}</span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Deductions</p>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">CPP</span>
                            <span>{fmt(p.cpp)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">EI</span>
                            <span>{fmt(p.ei)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Federal Tax</span>
                            <span>{fmt(p.federal_tax)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Provincial Tax</span>
                            <span>{fmt(p.provincial_tax)}</span>
                          </div>
                          <div className="flex justify-between font-semibold border-t border-gray-100 pt-1">
                            <span>Total Deductions</span>
                            <span>{fmt(p.total_deductions)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-[#C77D3F]/10 rounded-lg p-3 flex justify-between items-center">
                        <span className="font-bold text-[#C77D3F]">Net Pay</span>
                        <span className="text-xl font-bold text-[#C77D3F]">{fmt(p.net_pay)}</span>
                      </div>

                      {Number(p.ytd_gross) > 0 && (
                        <p className="text-xs text-gray-400 text-right">YTD Gross: {fmt(p.ytd_gross)}</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PullToRefresh>
  );
};
